/**
 * Typed API client for LabFlo.
 * All calls go through Zod validation so bad server data is caught early.
 */
import axios from 'axios';
import {
    LoginResponseSchema, UserSchema, TenantListItemSchema,
    SubscriptionStatusSchema, ReportDataSchema, AuditEntrySchema,
    safeValidate, type UserType, type TenantListItemType,
    type SubscriptionStatusType, type ReportDataType, type AuditEntryType,
} from './schemas';

export type { SubscriptionStatusType, UserType, TenantListItemType, ReportDataType, AuditEntryType };


const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Auth ──────────────────────────────────────────────────────────────────

export const authApi = {
    login: async (username: string, password: string) => {
        const r = await axios.post(`${BASE}/auth/login`, { username, password });
        return safeValidate(LoginResponseSchema, r.data, 'auth/login');
    },
    me: async (): Promise<UserType> => {
        const r = await axios.get(`${BASE}/auth/me`);
        return safeValidate(UserSchema, r.data, 'auth/me');
    },
};

// ─── Tenant ────────────────────────────────────────────────────────────────

export const tenantApi = {
    listActive: async (): Promise<TenantListItemType[]> => {
        const r = await axios.get(`${BASE}/tenant/list-active`);
        return (r.data as unknown[]).map(function(t, i) {
            return safeValidate(TenantListItemSchema, t, `tenant[${i}]`);
        });
    },
    subscriptionStatus: async (slug: string): Promise<SubscriptionStatusType> => {
        const r = await axios.get(`${BASE}/tenant/subscription-status`, {
            headers: { 'X-Tenant-Slug': slug },
        });
        return safeValidate(SubscriptionStatusSchema, r.data, 'subscription-status');
    },
};

// ─── Reports ───────────────────────────────────────────────────────────────

export const reportsApi = {
    generate: async (params: { report_type: string; start_date: string; end_date: string }): Promise<ReportDataType> => {
        const r = await axios.get(`${BASE}/reports`, { params });
        return safeValidate(ReportDataSchema, r.data, 'reports');
    },
    audit: async (params: { user_id?: string; start_date?: string; end_date?: string; limit?: number }): Promise<AuditEntryType[]> => {
        const r = await axios.get(`${BASE}/admin/audit/data-fields`, { params });
        return (r.data as unknown[]).map(function(e, i) {
            return safeValidate(AuditEntrySchema, e, `audit[${i}]`);
        });
    },
};
